import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaArrowRight,
  FaBolt,
  FaGamepad,
  FaGlobeAmericas,
  FaLink,
  FaLock,
  FaRecycle,
  FaShieldAlt,
  FaStar,
  FaWallet,
} from 'react-icons/fa';
import './Homepage.scss';
import Header from '../Header';
import Footer from '../Footer/Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import HomepageCard from '../../Common/HomepageCard/HomepageCard';
import TokenomicsCard from '../../Common/HomepageCard/TokenomicsCard';
import reddickRedNoBg from '../../../assets/reddickrednobackground.png';
import {
  ASTROLESCENT_BRIDGE_URL,
  BASE_BUY_URL,
  BASE_CHART_URL,
  BASE_DEXTOOLS_URL,
  BASE_LOCK_END_LABEL,
  BASE_LOCK_URL,
  BASE_PAIR_LABEL,
  BASE_PROOF_SUMMARY,
  DEX_SITE_URL,
  SITE_CONTENT,
  SWAP_SITE_URL,
} from '../../../config/siteContent';
import { useDckslapActivityStats } from '../../../dckslap/hooks/useDckslapActivityStats';
import { getCachedDckslapActivitySnapshot } from '../../../dckslap/services/activityStats';
import { getRoundedTreasuryTotalValueUSD } from '../TreasuryPage/treasuryValue';

const LANDING_INTRO_SESSION_KEY = 'rdk_intro_seen_v2';
const DEFAULT_ROUNDED_TREASURY_VALUE = 500000;

const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const observe = useCallback(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return observer;
  }, [threshold]);

  useEffect(() => {
    const observer = observe();
    return () => observer?.disconnect();
  }, [observe]);

  return [ref, isVisible];
};

const HERO_STAT_ITEMS = [
  { icon: <FaLock />, text: 'Base LP Locked' },
  { icon: <FaShieldAlt />, text: 'Team Finance Proof' },
  { icon: <FaGamepad />, text: 'DCKSlap Live' },
  { icon: <FaRecycle />, text: 'Fees Support Treasury' },
  { icon: <FaLink />, text: 'Radix + Base Live' },
];

const INTRO_BOXES = [
  {
    tag: 'Discover',
    title: 'RedDicks on Base',
    color: 'cyan',
    bullets: [
      { icon: '🔒', text: 'LP Locked on Base & Radix' },
      { icon: '♻️', text: '1% fees fuel the ecosystem' },
      { icon: '🌉', text: 'Cross-chain: Radix + Base' },
    ],
  },
  {
    tag: 'Tokenomics',
    title: 'Rug Proof by Design',
    color: 'crimson',
    bullets: [
      { icon: '🛡️', text: 'All liquidity locked — no rug' },
      { icon: '💰', text: 'Treasury owns the LP' },
      { icon: '📊', text: 'On-chain verifiable supply' },
    ],
  },
  {
    tag: 'Trade',
    title: 'Start Trading',
    color: 'green',
    bullets: [
      { icon: '⚡', text: 'Buy on Base via Uniswap' },
      { icon: '🔴', text: 'Buy on Radix via CaviarNine' },
      { icon: '🔗', text: 'Bridge via Astrolescent' },
    ],
  },
];

const INTRO_DURATION = 8000;
// Temporary kill switch: keep the intro modal implementation in place without showing it.
const ENABLE_LANDING_INTRO = false;

const Homepage = ({ enableIntroFlow = false }) => {
  const location = useLocation();
  const [showIntro, setShowIntro] = useState(false);
  const [introReady, setIntroReady] = useState(!enableIntroFlow || !ENABLE_LANDING_INTRO);
  const [introClosing, setIntroClosing] = useState(false);
  const [introActivityPreview] = useState(() => getCachedDckslapActivitySnapshot());
  const [roundedTreasuryValue, setRoundedTreasuryValue] = useState(DEFAULT_ROUNDED_TREASURY_VALUE);
  const {
    stats: activityStats,
    isLoading: isActivityLoading,
    error: activityError,
    hasCachedData: hasActivitySnapshot,
  } = useDckslapActivityStats();

  const [activityProofRef, activityProofVisible] = useScrollReveal(0.08);
  const [whyBaseRef, whyBaseVisible] = useScrollReveal(0.08);
  const [trustStripRef, trustStripVisible] = useScrollReveal(0.08);
  const [differentRef, differentVisible] = useScrollReveal(0.08);
  const [laterRef, laterVisible] = useScrollReveal(0.08);

  const isRootLanding = ENABLE_LANDING_INTRO && enableIntroFlow && location.pathname === '/';

  const dismissIntro = useCallback(() => {
    setIntroClosing(true);
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(LANDING_INTRO_SESSION_KEY, '1');
      }
      setShowIntro(false);
      setIntroClosing(false);
      setIntroReady(true);
    }, 400);
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadTreasuryValue = async () => {
      try {
        const roundedValue = await getRoundedTreasuryTotalValueUSD();
        if (isMounted && roundedValue > 0) {
          setRoundedTreasuryValue(roundedValue);
        }
      } catch (error) {
        console.warn('Failed to refresh homepage treasury teaser:', error);
      }
    };

    loadTreasuryValue();

    return () => {
      isMounted = false;
    };
  }, []);

  // Show/hide intro
  useEffect(() => {
    if (!isRootLanding) {
      setShowIntro(false);
      setIntroReady(true);
      return undefined;
    }

    const alreadySeen =
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(LANDING_INTRO_SESSION_KEY) === '1';

    if (alreadySeen) {
      setShowIntro(false);
      setIntroReady(true);
      return undefined;
    }

    setShowIntro(true);
    setIntroReady(false);
    return undefined;
  }, [isRootLanding]);

  // Auto-dismiss after duration
  useEffect(() => {
    if (!showIntro || introClosing) return undefined;
    const timer = setTimeout(dismissIntro, INTRO_DURATION);
    return () => clearTimeout(timer);
  }, [showIntro, introClosing, dismissIntro]);

  const hasActivityNumbers = hasActivitySnapshot || (!isActivityLoading && !activityError);
  const introPreviewStats = introActivityPreview?.stats || null;
  const activityStatusText = hasActivityNumbers
    ? 'Shared with the live leaderboard'
    : activityError
      ? 'Using fallback layout while live data refreshes'
      : 'Fetching the latest DCKSlap totals';
  const treasuryValueLabel = `$${roundedTreasuryValue.toLocaleString()}`;

  return (
    <div className="homepage" data-scroll-root>
      {showIntro && (
        <div className={`landing-intro-overlay ${introClosing ? 'is-closing' : 'is-visible'}`} role="dialog" aria-label="Welcome">
          <div className={`intro-card ${introClosing ? 'is-closing' : ''}`}>
            {/* Top bar: countdown + close */}
            <div className="intro-topbar">
              <div className="intro-countdown">
                <div className="intro-countdown__fill" style={{ animationDuration: `${INTRO_DURATION}ms` }} />
              </div>
              <button type="button" className="intro-close" onClick={dismissIntro} aria-label="Close">×</button>
            </div>

            {/* Header */}
            <h2 className="intro-title">Welcome to <span>REDDICKS</span></h2>
            <p className="intro-subtitle">Locked. Live. Running.</p>

            {introPreviewStats && (
              <div className="intro-activity-preview">
                <div className="intro-activity-preview__label">Live Utility Snapshot</div>
                <div className="intro-activity-preview__value">
                  {introPreviewStats.grandTotalVolume.toLocaleString()} total transactions
                </div>
                <div className="intro-activity-preview__support">DCKSlap on-chain transactions to date.</div>
              </div>
            )}

            {/* 3 info boxes */}
            <div className="intro-boxes">
              {INTRO_BOXES.map((box, idx) => (
                <div className={`intro-box intro-box--${box.color}`} key={box.tag} style={{ animationDelay: `${0.15 + idx * 0.12}s` }}>
                  <span className="intro-box__tag">{box.tag}</span>
                  <h3 className="intro-box__title">{box.title}</h3>
                  <ul className="intro-box__bullets">
                    {box.bullets.map((b) => (
                      <li key={b.text}>
                        <span className="intro-box__icon">{b.icon}</span>
                        <span>{b.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="intro-actions">
              <a href={BASE_BUY_URL} target="_blank" rel="noopener noreferrer" className="intro-cta intro-cta--primary" onClick={dismissIntro}>
                Buy on Base
              </a>
              <button type="button" className="intro-cta intro-cta--secondary" onClick={dismissIntro}>
                Explore Site
              </button>
            </div>
          </div>
        </div>
      )}

      <Header />

      <main className="homepage-content">
        <section className={`hp-hero ${introReady ? 'visible' : ''}`}>
          <div className="hero-bg-effects">
            <div className="hero-orb hero-orb--1"></div>
            <div className="hero-orb hero-orb--2"></div>
            <div className="hero-orb hero-orb--3"></div>
          </div>
          <div className="container">
            <div className="hero-content-wrapper">
              <div className="hero-text-content">
                <p className="hero-launch-note">
                  <strong>REDDICKS launched RSwap! 🔥</strong> Our new Radix DEX.{' '}
                  <a href={DEX_SITE_URL} target="_blank" rel="noopener noreferrer">
                    Check DEX
                  </a>{' '}
                  <a href={SWAP_SITE_URL} target="_blank" rel="noopener noreferrer">
                    Swap
                  </a>
                </p>
                <p className="hero-tagline">Base-First Entry, On-Chain Proof</p>
                <h1 className="hero-title">
                  <span className="hero-title__line">{SITE_CONTENT.hero.headline}</span>
                </h1>
                <div className="hero-mascot-wrapper">
                  <div className="hero-mascot-glow"></div>
                  <img src={reddickRedNoBg} alt="Reddicks mascot" className="hero-reddick-image" />
                </div>
                <p className="subtitle">{SITE_CONTENT.hero.subline}</p>
                <div className="hero-actions">
                  <a
                    href={BASE_BUY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-button hp-cta primary-cta"
                  >
                    Buy on Base
                  </a>
                  <a
                    href={BASE_CHART_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-button hp-cta secondary-cta"
                  >
                    View Chart
                  </a>
                </div>
                <p className="hero-funnel-note">
                  Base is the cleanest on-ramp. Team Finance and DEXTools now publish the live Base LP proof, while Radix keeps the deepest utility running.
                  <Link to="/dckslap" className="hero-funnel-note__link">Explore DCKSlap</Link>
                </p>
                <div className="hero-stat-strip">
                  {HERO_STAT_ITEMS.map((item) => (
                    <div className="hero-stat-pill" key={item.text}>
                      <span className="hero-stat-pill__icon">{item.icon}</span>
                      <span className="hero-stat-pill__text">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="hero-scroll-indicator">
            <span></span>
          </div>
        </section>

        <section ref={activityProofRef} className={`hp-activity-proof ${activityProofVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <div className="activity-proof-shell">
              <div className="activity-proof-copy">
                <span className="section-kicker">Live Utility Proof</span>
                <h2 className="section-title">Massive Activity. Visible Immediately.</h2>
                <p className="activity-proof-lead">DCKSlap already produces real on-chain usage.</p>
                <div className="activity-proof-actions">
                  <Link to="/dckslap/leaderboard" className="cta-button hp-cta primary-cta">
                    View Leaderboard
                  </Link>
                  <Link to="/dckslap" className="cta-button hp-cta secondary-cta">
                    Open DCKSlap
                  </Link>
                </div>
              </div>

              <div className="activity-proof-scoreboard">
                <div className="activity-proof-total">
                  <span className="activity-proof-total__label">Total Transactions</span>
                  <strong className="activity-proof-total__value">
                    {hasActivityNumbers ? activityStats.grandTotalVolume.toLocaleString() : '...'}
                  </strong>
                  <p className="activity-proof-total__note">{activityStatusText}</p>
                </div>
              </div>

              <div className="activity-proof-trust">
                <div className="activity-proof-trust__head">
                  <span className="activity-proof-trust__kicker">Base LP Proof</span>
                  <h3>{BASE_PAIR_LABEL}</h3>
                </div>
                <p>{BASE_PROOF_SUMMARY}</p>
                <div className="activity-proof-trust__meta">
                  <span>Lock visible until {BASE_LOCK_END_LABEL}</span>
                </div>
                <div className="activity-proof-trust__links">
                  <a href={BASE_LOCK_URL} target="_blank" rel="noopener noreferrer">
                    Team Finance
                  </a>
                  <a href={BASE_DEXTOOLS_URL} target="_blank" rel="noopener noreferrer">
                    DEXTools
                  </a>
                  <a href={BASE_CHART_URL} target="_blank" rel="noopener noreferrer">
                    Chart
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <HomepageCard />
        <TokenomicsCard />

        <section ref={whyBaseRef} className={`hp-base-why ${whyBaseVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <span className="section-kicker">Intentional Positioning</span>
            <h2 className="section-title centered">Why We&apos;re On Base</h2>
            <div className="base-why-grid">
              <article className="base-why-card base-why-card--cyan">
                <span className="base-why-card__number">01</span>
                <div className="base-why-card__icon"><FaWallet /></div>
                <h3>Coinbase-native liquidity</h3>
                <p>We want first impressions to be clean and credible for users already active in the Base ecosystem.</p>
              </article>
              <article className="base-why-card base-why-card--crimson">
                <span className="base-why-card__number">02</span>
                <div className="base-why-card__icon"><FaBolt /></div>
                <h3>High-velocity trading</h3>
                <p>1% fee structure routes trading value back into treasury-driven growth instead of leaking away.</p>
              </article>
              <article className="base-why-card base-why-card--violet">
                <span className="base-why-card__number">03</span>
                <div className="base-why-card__icon"><FaGlobeAmericas /></div>
                <h3>Built for expansion</h3>
                <p>Base is live today, Radix utility is live today, and Solana remains the next expansion lane.</p>
              </article>
              <article className="base-why-card base-why-card--green">
                <span className="base-why-card__number">04</span>
                <div className="base-why-card__icon"><FaLock /></div>
                <h3>Locked LP for trust</h3>
                <p>Base liquidity lock publication is now live on Team Finance with DEXTools as the supporting explorer.</p>
              </article>
            </div>
          </div>
        </section>

        <section ref={trustStripRef} className={`hp-trust-strip ${trustStripVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Built on Trust</h2>
            <div className="trust-strip-row">
              <div className="trust-strip-pill"><FaLock /> Team Finance Lock Live</div>
              <div className="trust-strip-pill"><FaRecycle /> Fees Go Back to Ecosystem</div>
              <div className="trust-strip-pill"><FaShieldAlt /> No Mint Function</div>
              <div className="trust-strip-pill"><FaStar /> Supply and Pair Public</div>
            </div>
            <div className="trust-strip-cta">
              <a href={BASE_LOCK_URL} target="_blank" rel="noopener noreferrer" className="cta-button hp-cta secondary-cta">
                View Base Lock
              </a>
              <Link to="/verify" className="cta-button hp-cta secondary-cta">
                Verify Everything <FaArrowRight />
              </Link>
            </div>
          </div>
        </section>

        <section ref={differentRef} className={`hp-different ${differentVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <span className="section-kicker">What Makes This Different</span>
            <h2 className="section-title centered">Not Just Another Meme</h2>
            <div className="different-grid">
              <article className="different-card different-card--cyan">
                <span className="different-card__number">01</span>
                <h3>Live utility on Radix and Base</h3>
                <p>REDDICKS is used across active products, not only traded as a ticker.</p>
              </article>
              <article className="different-card different-card--crimson">
                <span className="different-card__number">02</span>
                <h3>Fees fuel ecosystem growth</h3>
                <p>1% fee model is intentional: it funds treasury and long-term execution.</p>
              </article>
              <article className="different-card different-card--violet">
                <span className="different-card__number">03</span>
                <h3>Built for the long term</h3>
                <p>Lock visibility, trust repetition, and transparent disclosures come before hype.</p>
              </article>
            </div>
          </div>
        </section>

        <section ref={laterRef} className={`hp-later-tech ${laterVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Live Utility and Expansion</h2>
            <div className="later-tech-grid">
              <article className="later-tech-card">
                <h3>DCKBase Utility</h3>
                <p>Hold REDDICKS, mint DCKSLAPs, and use live game utility while Base liquidity stays live and publicly locked.</p>
                <span className="later-tech-card__meta">Live utility layer is active now.</span>
              </article>
              <article className="later-tech-card">
                <h3>Bridge and Buy Paths</h3>
                <p>Use Astrolescent for bridge access from Radix to Base, then confirm the official lock and explorer links before trading.</p>
                <span className="later-tech-card__meta">Cross-chain routing from Radix to Base.</span>
              </article>
              <article className="later-tech-card">
                <h3>Tokenomics and Trust</h3>
                <p>Tokenomics now separates Radix supply locks from Base LP proof so each trust layer is easy to inspect.</p>
                <span className="later-tech-card__meta">Transparency-first disclosure stack.</span>
              </article>
            </div>
            <div className="later-tech-route-grid">
              <a
                href={ASTROLESCENT_BRIDGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="later-tech-route-card later-tech-route-card--bridge"
              >
                <span className="later-tech-route-card__kicker">Bridge</span>
                <strong>Open Astrolescent</strong>
                <FaArrowRight />
              </a>
              <Link to="/buy" className="later-tech-route-card later-tech-route-card--buy">
                <span className="later-tech-route-card__kicker">Buy</span>
                <strong>Open Buy Routes</strong>
                <FaArrowRight />
              </Link>
              <Link to="/tokenomics" className="later-tech-route-card later-tech-route-card--tokenomics">
                <span className="later-tech-route-card__kicker">Tokenomics</span>
                <strong>View Tokenomics</strong>
                <FaArrowRight />
              </Link>
            </div>
            <div className="later-tech-dckslap-cta">
              <div className="treasury-spotlight">
                <div className="treasury-spotlight__seal" aria-hidden="true">
                  <FaWallet />
                </div>
                <div className="treasury-spotlight__copy">
                  <span className="treasury-spotlight__kicker">Treasury Ledger</span>
                  <p>Locked LP, reserve holdings, and long-term allocation context.</p>
                  <div className="treasury-spotlight__value-block">
                    <span className="treasury-spotlight__value-label">Approx. Treasury Value</span>
                    <strong className="treasury-spotlight__value-amount">{treasuryValueLabel}</strong>
                  </div>
                  <div className="treasury-spotlight__meta" aria-hidden="true">
                    <span>Locked LP</span>
                    <span>Holdings</span>
                    <span>Endowment</span>
                  </div>
                </div>
                <Link to="/treasury" className="treasury-spotlight__button">
                  <span className="treasury-spotlight__button-kicker">Vault Access</span>
                  <span className="treasury-spotlight__button-label">View Treasury</span>
                  <FaArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Homepage;
